/**
 * The base vocabulary (data/phrases.json; SpeiseKarte S36, Martin 01.11.2026): common short words of
 * German menus in every language of the cards – sections and categories, the cards, courses, the
 * drinks' categories a business begins with, labels of prices and portions, the additives 1–14 (not
 * set by the EU, unlike the 14 allergens; the same as in food's allergens.json), some tags. Checked
 * once by us and shipped with the apps; no model, no waiting.
 *
 * Rules (Martin): the business's own translation always wins, also when it reads the same. The base is
 * never copied into a business's dictionary but looked up each time, so a correction here holds with
 * the next copy wherever the field still showed the base. Only exact words, no sentences, nothing
 * whose meaning depends on the house („Menü“: a set menu or the card?). Never filled from the texts
 * of the businesses: those are theirs. The dictionary of a business stays with its app.
 *
 * Two parts. `BASE_PHRASES` holds in any context and serves every card. `DRINK_PHRASES` holds only on
 * a drinks card: „Weizen“ is wheat or a wheat beer, „Halbe“ half a litre of beer or half a portion,
 * „Dunkel“, „süß“, „Dose“, „Magnum“ … the same. They answer only with `{ drinks: true }`; KellerLog
 * always asks so, SpeiseKarte for now never (a business's allergen renamed „Weizen“ is no beer).
 */
import data from "../data/phrases.json" with { type: "json" };
import type { Lang } from "./langs.ts";

/** The German text as a key: one line, composed. */
export const phraseKey = (s: string) => s.normalize("NFC").replace(/\s+/g, " ").trim();

type Table = Readonly<Record<string, Readonly<Record<Lang, string>>>>;

/** By the German text (`phraseKey`), its translation per language: the words clear in any context. */
export const BASE_PHRASES: Table = data.phrases;
/** The same for words clear only on a drinks card („Weizen“, „Halbe“, „Dunkel“); never a key of BASE_PHRASES. */
export const DRINK_PHRASES: Table = data.drinks;

/** Where a text stands: `drinks` also on a drinks card's words. */
export interface PhraseOptions {
  drinks?: boolean;
}

const lookUp = (table: Table, key: string, lang: Lang) => (Object.hasOwn(table, key) ? table[key]![lang] : undefined);

/** The base vocabulary's translation of a German text, or undefined; a drinks card's words only with `drinks`. */
export function basePhrase(text: string, lang: Lang, { drinks = false }: PhraseOptions = {}): string | undefined {
  const key = phraseKey(text);
  return lookUp(BASE_PHRASES, key, lang) ?? (drinks ? lookUp(DRINK_PHRASES, key, lang) : undefined);
}

/** A business's dictionary as the app keeps it: by the German text (`phraseKey`), its translations. */
export type OwnPhrases<P extends { text: string }> = Readonly<Record<string, Partial<Record<Lang, P>>>>;

/** A translation found in the base vocabulary. */
export type BasePhrase = { text: string; by: "base" };

/** A text's translation into the language: the business's own always first (when not empty), then the base. */
export function phraseOf<P extends { text: string }>(text: string, lang: Lang, own: OwnPhrases<P> = {}, options: PhraseOptions = {}): P | BasePhrase | undefined {
  const key = phraseKey(text);
  const mine = Object.hasOwn(own, key) ? own[key]?.[lang] : undefined;
  if (mine?.text) return mine;
  const base = basePhrase(key, lang, options);
  return base ? { text: base, by: "base" } : undefined;
}
